'use client'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import CustomDrawerModal from '../common/CustomDrawerModal'
import ActionButton from '../common/ActionButton'
import { PatientDataObj, UserForm } from '@/utils/interfaces'
import { dispatch, useSelector } from '@/lib/store'
import {
  CREATE_FAMILY_MEMBER,
  CREATE_PATIENT_API,
  SEND_OTP,
  UPDATE_PATIENT_API,
  VALIDATE_OTP,
} from '@/helper/apiConstants'
import { consumerRequest } from '@/helper/request'
import { useEffect, useState } from 'react'
import useCustomToast from '@/hooks/useCustomToast'
import { Input } from '../ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { memberOnboardingSchema, memberFamilySchema } from '@/utils/validationSchemas'
import {
  calculateAge,
  formatDateString,
  getAllPrimaryProfiles,
  isEmpty,
} from '@/utils/commonFunc'
import PATIENT_HELPER from '@/helper/patientHelper'
import { updateUserProfile } from '@/lib/reducers/userReducer'
import { DatePicker } from '@/components/ui/date-picker'
import { resetLoginData, updateLoginData } from '@/lib/reducers/loginReducer'
import { updateCommonReducerData } from '@/lib/reducers/commonReducer'
import { loadState, saveState } from '@/utils/localStorage'
import moment from 'moment'
import InfoBox from '@/components/common/InfoBox'
import OtpInputBlock from '@/components/common/OtpInputBlock'
import SelectMultipleMemberModal from '../common/SelectMultipleMemberModal'
import { updateEventUser } from '@/utils/events'

const AddNewMemberModal = ({
  open,
  onOpenChange,
  onBoarding,
  patientDetails,
}: any) => {
  const showToast = useCustomToast()

  const { handleGetUserDetails } = PATIENT_HELPER()
  const loggedInMobileNumber = loadState('ctxVal') || null

  const cityState = useSelector((state) => state.city)
  const userState = useSelector((state) => state.user)
  const loginState = useSelector((state) => state?.login) || {}
  const commonState = useSelector((state) => state.common) || {}

  const [edit, setEdit] = useState(!isEmpty(patientDetails))
  const [loading, setLoading] = useState(false)
  const [userForm, setUserForm] = useState<UserForm>({
    firstName: '',
    lastName: '',
    dob: '',
    phoneNumber: '',
    gender: '',
    email: '',
    relation: '',
  })
  const [openOTPModal, setOpenOTPModal] = useState<boolean>(false)
  const [isVerified, setIsVerified] = useState(false)
  const [alreadyRegisteredMembers, setAlreadyRegisteredMembers] = useState<any>(
    []
  )
  const [
    alreadyRegisteredMembersModalOpen,
    setAlreadyRegisteredMembersModalOpen,
  ] = useState<boolean>(false)
  const [resetCount, setResetCount] = useState<boolean>(false)

  const primaryProfile = getAllPrimaryProfiles(userState?.membersList)?.[0]

  useEffect(() => {
    if (!onBoarding && !edit) {
      setUserForm((prev) => ({
        ...prev,
        relation: '',
      }))
    }
  }, [onBoarding])

  useEffect(() => {
    if (edit) {
      const obj: UserForm = {
        firstName: patientDetails?.firstName,
        lastName: patientDetails?.lastName,
        dob: patientDetails?.dob,
        phoneNumber: patientDetails?.phoneNumber,
        gender: patientDetails?.gender,
        email: patientDetails?.email,
        relation: patientDetails?.relation,
      }

      setUserForm({ ...obj })
      Object.keys(obj).length > 0 &&
        Object.keys(obj).map((item: string, index) => {
          setValue(item as keyof UserForm, Object.values(obj)[index])
        })
    }
  }, [edit])

  const validationSchema = onBoarding ? memberOnboardingSchema : memberFamilySchema

  const {
    register,
    handleSubmit,
    clearErrors,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      dob: '',
      phoneNumber: '',
      gender: '',
      relation: '',
      email: '',
    },
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  })

  const handleInputChange = (name: string, value: string) => {
    setUserForm((prevForm) => ({
      ...prevForm,
      [name]: value,
    }))
  }

  const onSubmit: SubmitHandler<UserForm> = async (data) => {
    if (
      !edit &&
      !isEmpty(data?.phoneNumber) &&
      ((data?.phoneNumber && data?.phoneNumber?.length < 10) || !isVerified)
    ) {
      showToast('danger', 'Please verify mobile number first.')
      return
    }
    setLoading(true)
    const formData = {
      age: (calculateAge(data?.dob) || 0)?.toString(),
      cityUuid: cityState?.currentCityObj?.uuid,
      dob: data?.dob,
      email: data?.email,
      firstName: data?.firstName,
      gender: data?.gender,
      lastName: data?.lastName,
      phoneNumber: onBoarding
        ? loginState?.phoneNumber
        : data?.phoneNumber || loggedInMobileNumber,
      relation: onBoarding ? 'SELF' : data?.relation,
      code: edit ? patientDetails?.code : '',
    }
    if (edit) {
      const updateFormData = {
        emailId: formData?.email,
        loggedInPhoneNumber: loggedInMobileNumber,
        patientCode: formData?.code,
        patientFirstName: formData?.firstName,
        patientPhoneNumber: formData?.phoneNumber,
        primaryMemberPatientCode: primaryProfile?.code,
        relation: formData?.relation,
      }

      const apiRes: any = await consumerRequest(
        UPDATE_PATIENT_API,
        'post',
        updateFormData
      )
      if (apiRes?.status) {
        await handleGetUserDetails()
        showToast('success', apiRes?.message)
        onOpenChange(false)
      } else {
        showToast('danger', apiRes?.message)
      }
    } else {
      if (onBoarding) {
        const apiRes: any = await consumerRequest(
          CREATE_PATIENT_API,
          'post',
          formData
        )
        if (apiRes?.status === true) {
          dispatch(updateLoginData({ ...loginState, loginModalIsOpen: false }))
          dispatch(updateUserProfile({ ...formData, code: apiRes?.data }))
          dispatch(
            updateCommonReducerData({
              ...commonState,
              userOnboardingModalIsOpen: false,
            })
          )
          saveState('user', { ...formData, code: apiRes?.data })
          saveState('ctxVal', loginState?.phoneNumber)

          const user = {
            firstName: formData.firstName,
            lastName: formData.lastName || '',
            dob: formData?.dob,
            phoneNumber: formData?.phoneNumber,
            gender: formData?.gender,
            code: apiRes?.data,
            relation: formData?.relation,
          }
          updateEventUser(user)
        } else {
          dispatch(resetLoginData())
          console.error('Failed to create patient', apiRes?.message)
          showToast('danger', `${apiRes?.message}`)
        }
      } else {
        const familyFormData = {
          ...formData,
          primaryMemberPatientCode: primaryProfile?.code,
          primaryMemberPhoneNumber: primaryProfile?.phoneNumber,
        }

        const apiRes: any = await consumerRequest(
          CREATE_FAMILY_MEMBER,
          'post',
          familyFormData
        )

        if (apiRes?.status === true) {
          if (
            apiRes?.data?.alreadyRegisteredMobileNumber &&
            !isEmpty(apiRes?.data?.patients)
          ) {
            const members: Array<Object> = []
            apiRes?.data?.patients?.map((singleMember: PatientDataObj) => {
              members.push({
                ...singleMember,
                relation: null,
                primaryMemberPatientCode: primaryProfile?.code,
                primaryMemberPhoneNumber: primaryProfile?.phoneNumber,
              })
            })
            setOpenOTPModal(false)
            setAlreadyRegisteredMembers(members)
            setAlreadyRegisteredMembersModalOpen(true)
          } else if (
            apiRes?.data?.alreadyRegisteredMobileNumber &&
            isEmpty(apiRes?.data?.patients)
          ) {
            onOpenChange(false)
            showToast(
              'danger',
              'All profiles with this number are already linked.'
            )
          } else {
            await handleGetUserDetails()
            onOpenChange(false)
            showToast('success', apiRes?.message)
          }
        } else {
          console.error('Failed to create patient', apiRes?.message)
          showToast('danger', `${apiRes?.message}`)
        }
      }
    }
    setLoading(false)
  }

  const handleVerify = async () => {
    if (userForm?.phoneNumber?.length != 10) {
      showToast('danger', `Phone number must be at least 10 digits`)
      return
    }
    if (userForm?.phoneNumber === loggedInMobileNumber) {
      setIsVerified(true)
      return
    }
    setLoading(true)
    const formData = {
      otpType: 'ADD_FAMILY_MEMBER',
      phoneNumber: userForm?.phoneNumber,
      cityId: cityState?.currentCityObj?.uuid,
      primaryMemberName: `${primaryProfile?.firstName || ''} ${
        primaryProfile?.lastName || ''
      }`.trim(),
    }

    const apiRes: any = await consumerRequest(SEND_OTP, 'post', formData)
    if (apiRes?.status === true) {
      setOpenOTPModal(true)
    } else {
      showToast('danger', apiRes?.message)
    }
    setLoading(false)
  }

  const onResendOTP = async () => {
    const formData = {
      otpType: 'ADD_FAMILY_MEMBER',
      phoneNumber: userForm?.phoneNumber,
      cityId: cityState?.currentCityObj?.uuid,
      primaryMemberName: `${primaryProfile?.firstName || ''} ${
        primaryProfile?.lastName || ''
      }`.trim(),
    }

    const apiRes: any = await consumerRequest(SEND_OTP, 'post', formData)
    if (apiRes?.status === false) {
      showToast('danger', apiRes?.message)
    } else {
      showToast('success', apiRes?.message)
      setResetCount(true)
    }
  }

  const onSubmitOTP = async (otpInput: any) => {
    const formData = {
      otp: otpInput?.join(''),
      otpType: 'ADD_FAMILY_MEMBER',
      phoneNumber: userForm?.phoneNumber,
    }

    const apiRes: any = await consumerRequest(VALIDATE_OTP, 'post', formData)
    if (apiRes?.status === true) {
      setOpenOTPModal(false)
      setIsVerified(true)
    } else {
      showToast('danger', apiRes?.message)
    }
  }

  const handleChangeNumber = async () => {
    setIsVerified(false)
  }

  return (
    <>
      <CustomDrawerModal
        open={open}
        title={
          onBoarding
            ? 'Let’s get to know you'
            : edit
            ? 'Update member details'
            : 'Add a new member'
        }
        onOpenChange={onOpenChange}
        dialogContentClassName="max-h-dvh"
      >
        {!!onBoarding && <InfoBox />}
        <div className="p-1 overflow-y-auto max-h-[calc(100dvh-120px)]">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid gap-4 mb-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-label-xs text-gray-700 mb-1">
                    First name*
                  </Label>
                  <Input
                    {...register('firstName', { required: true })}
                    type="text"
                    value={userForm.firstName}
                    placeholder="First name"
                    className="w-full bg-gray-100"
                    onChange={(e) => {
                      handleInputChange('firstName', e.target.value)
                      setValue('firstName', e.target.value)
                      clearErrors('firstName')
                    }}
                    disabled={edit}
                  />
                  {errors.firstName?.message && (
                    <p className="text-label-xs text-danger-500 mb-0 mt-1 h-4">
                      {errors.firstName?.message}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-label-xs text-gray-700 mb-1">
                    Last name
                  </Label>
                  <Input
                    {...register('lastName', { required: false })}
                    type="text"
                    value={userForm?.lastName || ''}
                    placeholder="Last name"
                    className="w-full bg-gray-100"
                    onChange={(e) => {
                      handleInputChange('lastName', e.target.value)
                      setValue('lastName', e.target.value)
                    }}
                    disabled={edit}
                  />
                  {errors.lastName?.message && (
                    <p className="text-label-xs text-danger-500 mb-0 mt-1 h-4">
                      {errors.lastName?.message}
                    </p>
                  )}
                </div>
              </div>
              <div
                className={`grid  gap-4 ${
                  onBoarding ? 'grid-cols-1' : 'grid-cols-2'
                }`}
              >
                {!onBoarding && (
                  <div className="basis-[48%]">
                    <Label
                      className="text-label-xs text-gray-700 mb-1"
                      htmlFor="relation"
                    >
                      Select relation*
                    </Label>
                    <Select
                      {...register('relation')}
                      value={userForm?.relation}
                      onValueChange={(value) => {
                        handleInputChange('relation', value)
                        setValue('relation', value)
                        clearErrors('relation')
                      }}
                    >
                      <SelectTrigger className="bg-gray-100">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent id="relation">
                        <SelectItem value="SELF">Self</SelectItem>
                        <SelectItem value="FATHER">Father</SelectItem>
                        <SelectItem value="MOTHER">Mother</SelectItem>
                        <SelectItem value="SISTER">Sister</SelectItem>
                        <SelectItem value="BROTHER">Brother</SelectItem>
                        <SelectItem value="SPOUSE">Spouse</SelectItem>
                        <SelectItem value="SON">Son</SelectItem>
                        <SelectItem value="DAUGHTER">Daughter</SelectItem>
                        <SelectItem value="OTHER">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.relation?.message && (
                      <p className="text-label-xs text-danger-500 mb-0 mt-1 h-4">
                        {errors.relation?.message}
                      </p>
                    )}
                  </div>
                )}
                <div className={`${onBoarding ? 'w-full' : 'basis-[48%]'}`}>
                  <Label className=" text-label-xs text-gray-700 mb-1">
                    Date of birth*
                  </Label>
                  {edit ? (
                    <Input
                      className="w-full bg-gray-100"
                      type="text"
                      value={moment(userForm.dob).format('DD/MM/YYYY')}
                      disabled
                      id="dob"
                    />
                  ) : (
                    <DatePicker
                      {...register('dob', { required: true })}
                      id="dob"
                      date={userForm.dob}
                      onDateChange={(selectedDate) => {
                        if (selectedDate) {
                          const formattedDate = formatDateString(
                            new Date(selectedDate).toDateString()
                          )
                          handleInputChange('dob', formattedDate)
                          setValue('dob', formattedDate)
                          clearErrors('dob')
                        }
                      }}
                      placeholder="dd/mm/yyyy"
                      dateFormat="DD/MM/YYYY"
                      fromYear={1900}
                      toYear={new Date().getFullYear()}
                      className="w-full bg-gray-100"
                    />
                  )}
                  {errors.dob?.message && (
                    <p className="text-label-xs text-danger-500 mb-0 mt-1 h-4">
                      {errors.dob?.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {!onBoarding && (
              <div className="mb-4">
                <Label className="text-label-xs text-gray-700 mb-1">
                  Mobile Number
                </Label>
                <div className="relative">
                  <Input
                    {...register('phoneNumber', { required: false })}
                    type="number"
                    value={userForm?.phoneNumber || ''}
                    className="w-full bg-gray-100"
                    onScroll={(e) => e.preventDefault()}
                    // onResize={(e) => e.preventDefault()}
                    onKeyDown={(e) => {
                      if (
                        e.key === 'e' ||
                        e.key === 'E' ||
                        e.key === '+' ||
                        e.key === '-'
                      ) {
                        e.preventDefault()
                      }
                    }}
                    onChange={(e) => {
                      // Ensure the value doesn't exceed 10 digits
                      if (e.target.value.length >= 10) {
                        e.target.value = e.target.value.slice(0, 10)
                      }
                      handleInputChange('phoneNumber', e.target.value)
                      setValue('phoneNumber', e.target.value)
                      clearErrors('phoneNumber')
                    }}
                    placeholder="Enter Mobile Number"
                    disabled={isVerified || edit}
                  />
                  {!edit && (
                    <div className="absolute right-0 top-0">
                      {!isVerified ? (
                        <button
                          type="button"
                          className="text-label-sm font-semibold text-primary px-4 py-1 h-[50px]"
                          onClick={handleVerify}
                        >
                          Verify
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="text-label-sm font-semibold text-primary px-4 py-1 h-[50px]"
                          onClick={handleChangeNumber}
                        >
                          Change
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {errors.phoneNumber?.message ? (
                  <p className="text-label-xs text-danger-500 mb-0 mt-1 h-4">
                    {errors.phoneNumber?.message}
                  </p>
                ) : userForm?.phoneNumber &&
                  userForm?.phoneNumber != loggedInMobileNumber ? (
                  <p className="text-label-xs text-gray-700 mb-0 mt-1 h-4">
                    {`We'll send an OTP to verify.`}
                  </p>
                ) : (
                  ''
                )}
              </div>
            )}
            <div className="w-full mb-4">
              <Label
                htmlFor="gender"
                className="text-label-xs text-gray-700 mb-1"
              >
                Select Gender*
              </Label>
              <RadioGroup
                id="gender"
                value={userForm.gender}
                className="flex items-center gap-2 mt-3"
                onValueChange={(value) => {
                  if (edit) return
                  handleInputChange('gender', value)
                  setValue('gender', value)
                  clearErrors('gender')
                }}
              >
                <div className="flex items-center">
                  <RadioGroupItem value="MALE" id="MALE" className="hidden" />
                  <Label
                    htmlFor="MALE"
                    className={`cursor-pointer text-label-sm px-3 py-2 rounded-lg ${
                      userForm.gender === 'MALE'
                        ? 'text-primary bg-primary-100'
                        : 'text-gray-800 bg-gray-100'
                    }`}
                  >
                    Male
                  </Label>
                </div>
                <div className="flex items-center">
                  <RadioGroupItem
                    value="FEMALE"
                    id="FEMALE"
                    className="hidden"
                  />
                  <Label
                    htmlFor="FEMALE"
                    className={`cursor-pointer text-label-sm px-3 py-2 rounded-lg ${
                      userForm.gender === 'FEMALE'
                        ? 'text-primary bg-primary-100'
                        : 'text-gray-800 bg-gray-100'
                    }`}
                  >
                    Female
                  </Label>
                </div>
                <div className="flex items-center">
                  <RadioGroupItem
                    value="OTHERS"
                    id="OTHERS"
                    className="hidden"
                  />
                  <Label
                    htmlFor="OTHERS"
                    className={`cursor-pointer text-label-sm px-3 py-2 rounded-lg ${
                      userForm.gender === 'OTHERS'
                        ? 'text-primary bg-primary-100'
                        : 'text-gray-800 bg-gray-100'
                    }`}
                  >
                    Other
                  </Label>
                </div>
              </RadioGroup>
              {errors.gender?.message && (
                <p className="text-label-xs text-danger-500 mb-0 mt-1 h-4">
                  {errors.gender?.message}
                </p>
              )}
            </div>
            {!onBoarding && (
              <div className="mb-6">
                <Label className="text-label-xs text-gray-700 mb-1">
                  Email
                </Label>
                <Input
                  {...register('email', { required: false })}
                  type="email"
                  value={userForm?.email || ''}
                  className="w-full bg-gray-100"
                  onChange={(e) => {
                    handleInputChange('email', e.target.value)
                    setValue('email', e.target.value)
                  }}
                  placeholder="Enter Your Email"
                />
                {errors.email?.message && (
                  <p className="text-label-xs text-danger-500 mb-0 mt-1 h-4">
                    {errors.email?.message}
                  </p>
                )}
              </div>
            )}
            {!onBoarding && (
              <div className="mb-6">
                <InfoBox
                  infoText={
                    'Provided number will be used for all booking communications'
                  }
                />
              </div>
            )}
            <ActionButton variant="primary"
              title={
                onBoarding
                  ? 'Explore DDRC Agilus'
                  : edit
                  ? 'Update member profile'
                  : 'Create member profile'
              }
              type="submit"
              loading={loading}
              disabled={loading}
              className="text-label-lg font-semibold p-3 w-full"
            />
          </form>
        </div>
      </CustomDrawerModal>
      <CustomDrawerModal
        open={openOTPModal}
        title={'Verify mobile number'}
        onOpenChange={() => {
          setOpenOTPModal(false)
        }}
      >
        <OtpInputBlock
          loading={loading}
          onResendOTP={onResendOTP}
          onSubmitOTP={onSubmitOTP}
          resetCount={resetCount}
        />
      </CustomDrawerModal>
      <SelectMultipleMemberModal
        open={alreadyRegisteredMembersModalOpen}
        onOpenChange={() => {
          setAlreadyRegisteredMembersModalOpen(false)
        }}
        mappingSuccessHandler={() => {
          setAlreadyRegisteredMembersModalOpen(false)
          onOpenChange(false)
        }}
        mobile={userForm?.phoneNumber}
        alreadyRegisteredMembers={alreadyRegisteredMembers}
      />
    </>
  )
}

export default AddNewMemberModal
